/**
 * Exchange book maths.
 *
 * Everything the exposure screens draw comes from here, so it is pure, has no
 * React or formatting in it, and is unit-tested against hand-worked examples.
 *
 * Sign convention, used without exception across the console:
 *
 *   A number is stated from the BOOK's point of view — the operator running
 *   this console is the counterparty to their members' bets. Positive is money
 *   the book keeps, negative is money the book pays out.
 *
 * A member's own exposure is the mirror image, and `memberExposure` does that
 * flip in one place so no screen ever hand-rolls a minus sign.
 */

import type { Account, Bet, BetSide, BookPosition } from '@/data/types'

/** The minimum a bet needs for book maths — lets tests build cases inline. */
export interface BookLeg {
  selection: string
  side: BetSide
  /** Matched stake. Unmatched money carries no liability and is excluded. */
  matched: number
  /** Decimal odds the stake matched at. */
  odds: number
}

export const legOf = (bet: Bet): BookLeg => ({
  selection: bet.selection,
  side: bet.side,
  matched: bet.matched,
  odds: bet.oddsMatched ?? bet.oddsPlaced,
})

/**
 * The book's position on each selection, i.e. what the book ends up with if
 * that selection wins.
 *
 * For a BACK bet by a member on selection j at decimal odds o, matched m:
 *   - j wins  -> the member is paid m*(o-1), so the book is -m*(o-1)
 *   - j loses -> the member forfeits m, so the book is +m
 *
 * For a LAY bet by a member on selection j (the member is laying j):
 *   - j wins  -> the member pays m*(o-1), so the book is +m*(o-1)
 *   - j loses -> the member is paid m, so the book is -m
 *
 * Selections a member never touched still accumulate the "loses" side of every
 * other leg, which is exactly why a book can be positive everywhere or negative
 * everywhere.
 */
export function bookBySelection(legs: BookLeg[], selections: string[]): Map<string, number> {
  const net = new Map<string, number>(selections.map((s) => [s, 0]))

  for (const leg of legs) {
    if (!(leg.matched > 0)) continue
    const win = leg.matched * (leg.odds - 1)
    for (const sel of selections) {
      const current = net.get(sel) ?? 0
      const isSubject = sel === leg.selection
      if (leg.side === 'back') {
        net.set(sel, current + (isSubject ? -win : leg.matched))
      } else {
        net.set(sel, current + (isSubject ? win : -leg.matched))
      }
    }
  }

  return net
}

export interface BookSummary {
  /** Position per selection, in the order the selections were given. */
  positions: { name: string; net: number }[]
  /** The book's result under its least favourable outcome. */
  worstCase: number
  /** The book's result under its most favourable outcome. */
  bestCase: number
  /** Total matched money across the legs. */
  matched: number
}

export function summariseBook(legs: BookLeg[], selections: string[]): BookSummary {
  const net = bookBySelection(legs, selections)
  const positions = selections.map((name) => ({ name, net: net.get(name) ?? 0 }))
  const values = positions.map((p) => p.net)
  return {
    positions,
    worstCase: values.length ? Math.min(...values) : 0,
    bestCase: values.length ? Math.max(...values) : 0,
    matched: legs.reduce((sum, l) => sum + l.matched, 0),
  }
}

/**
 * A member's worst case, as a positive "how much could this cost them" number.
 *
 * This is the figure that gates a bet against a credit limit, so it is stated
 * as a magnitude: 0 means no risk, 5000 means they could be down ₹5,000.
 */
export function memberExposure(legs: BookLeg[], selections: string[]): number {
  const { bestCase } = summariseBook(legs, selections)
  // The book's best case is the member's worst case.
  return Math.max(0, bestCase)
}

/** Liability a single leg carries for the member who placed it. */
export function legLiability(leg: BookLeg): number {
  return leg.side === 'back' ? leg.matched : leg.matched * (leg.odds - 1)
}

/** Profit a single leg returns to the member if it wins, net of stake. */
export function legProfit(leg: BookLeg): number {
  return leg.side === 'back' ? leg.matched * (leg.odds - 1) : leg.matched
}

/* ------------------------------------------------------- hierarchy split -- */

/**
 * Split a settled amount up a chain of agents by share percentage.
 *
 * `chain` is ordered from the member's direct parent upward to the root, each
 * entry carrying that agent's share in percentage points. Shares are normalised
 * so they always total the amount exactly — an operator misconfiguring shares to
 * 97% or 103% must not silently create or destroy money on a report.
 *
 * `amount` is the book's result (positive = the book won), so each agent's
 * slice carries the same sign.
 */
export function splitByShare<T extends { id: string; share: number }>(
  amount: number,
  chain: T[],
): { id: string; share: number; amount: number }[] {
  const total = chain.reduce((s, c) => s + c.share, 0)
  if (chain.length === 0) return []
  if (total <= 0) {
    // Degenerate config: hand it all to the direct parent rather than vanish it.
    const first = chain[0]
    return chain.map((c, i) => ({ id: c.id, share: 0, amount: i === 0 && first ? amount : 0 }))
  }

  const raw = chain.map((c) => ({ id: c.id, share: c.share, amount: (amount * c.share) / total }))
  // Push rounding residue onto the largest slice so the parts sum to the whole.
  const rounded = raw.map((r) => ({ ...r, amount: round2(r.amount) }))
  const residue = round2(amount - rounded.reduce((s, r) => s + r.amount, 0))
  if (residue !== 0) {
    let biggest = 0
    for (let i = 1; i < rounded.length; i++) {
      if (Math.abs(rounded[i]!.amount) > Math.abs(rounded[biggest]!.amount)) biggest = i
    }
    rounded[biggest]!.amount = round2(rounded[biggest]!.amount + residue)
  }
  return rounded
}

export const round2 = (n: number): number => Math.round(n * 100) / 100

/* ------------------------------------------------------------- headroom -- */

/**
 * How much more risk an account can take before its credit limit rejects bets.
 * Returned as a ratio so a progress bar and a threshold test share one number.
 */
export function limitUsage(exposure: number, creditLimit: number): number {
  if (!(creditLimit > 0)) return exposure > 0 ? 1 : 0
  return exposure / creditLimit
}

/** How much an agent can still hand down without over-committing themselves. */
export function availableToGive(balance: number, exposure: number): number {
  return Math.max(0, round2(balance - exposure))
}

/**
 * Round DOWN to the paise.
 *
 * Every cap on this page is floored, never rounded: `round2(12.345)` is 12.35,
 * which is a paise MORE than the cap it was meant to respect, and the ledger
 * rejects it. A capped figure that fails at submit is worse than one that is a
 * paise short.
 *
 * The epsilon is not decoration. `8.29 * 100` is 828.9999999999999 in IEEE 754,
 * so a naive floor turns an already-2dp figure into 8.28 and silently loses a
 * paise off every clean amount that passes through.
 */
export const floor2 = (n: number): number => {
  const scaled = n * 100
  const nearest = Math.round(scaled)
  return (Math.abs(scaled - nearest) < 1e-6 ? nearest : Math.floor(scaled)) / 100
}

/* ------------------------------------------------------------ exchange ticks -- */

/**
 * The price increments the exchange actually trades on.
 *
 * This lived in `world.ts` until the market-detail page needed it too. Tick
 * maths is pure and belongs with the rest of the book maths, where `book.test.ts`
 * can pin the band boundaries — a second copy of this table in a prices module
 * is how the two drift, and a simulated ladder that lands between ticks reads as
 * obviously fake for the wrong reason.
 */
const TICK_BANDS: [lo: number, hi: number, step: number][] = [
  [1.01, 2, 0.01],
  [2, 3, 0.02],
  [3, 4, 0.05],
  [4, 6, 0.1],
  [6, 10, 0.2],
  [10, 20, 0.5],
  [20, 1000, 1],
]

const bandFor = (odds: number): number => {
  // Below the tradable floor, use the finest band: `tick` clamps the result to
  // 1.01 anyway, and falling through to the coarsest band would make a
  // sub-minimum price step by 1.00.
  if (odds < TICK_BANDS[0]![0]) return TICK_BANDS[0]![2]
  const band = TICK_BANDS.find(([lo, hi]) => odds >= lo && odds < hi) ?? TICK_BANDS[TICK_BANDS.length - 1]!
  return band[2]
}

/** Snap to the nearest tradable price. Never below the 1.01 floor. */
export function tick(odds: number): number {
  const step = bandFor(odds)
  return round2(Math.max(1.01, Math.round(odds / step) * step))
}

/** `steps` ticks up from `odds`, re-snapping at every band boundary it crosses. */
export function tickUp(odds: number, steps = 1): number {
  let value = tick(odds)
  for (let i = 0; i < steps; i++) value = tick(round2(value + bandFor(value)))
  return value
}

/**
 * `steps` ticks down from `odds`, floored at 1.01.
 *
 * Going down, the step is the one belonging to the band BELOW the boundary —
 * one tick down from 3.00 is 2.98, not 2.95, because 3.00 is the first price of
 * the coarser band, not the last price of the finer one.
 */
export function tickDown(odds: number, steps = 1): number {
  let value = tick(odds)
  for (let i = 0; i < steps; i++) {
    // The probe is deliberately NOT rounded: `round2(2 - 0.001)` is 2.00, which
    // reads the boundary's own band back and steps down twice.
    value = tick(Math.max(1.01, round2(value - bandFor(value - 0.001))))
  }
  return value
}

/* --------------------------------------------------------------- hedging -- */

export interface HedgeResult {
  selection: string
  price: number
  /** Stake required. 0 when the target is already met — never a negative bet. */
  stake: number
  /** The book after the bet, in the input order. */
  positions: BookPosition[]
  /** min over `positions` — the real worst case AFTER the bet. */
  worstCase: number
  /** True when worstCase < target: the target lifted one runner past another. */
  overshoots: boolean
}

/**
 * What backing `selection` at `price` does to the book, and what it costs to
 * bring that selection to `target`.
 *
 * The operator BACKS selection w at decimal odds o for stake k:
 *   w wins  -> they are paid k(o-1)  ->  B'w = Bw + k(o-1)
 *   w loses -> they lose k           ->  B'i = Bi - k   for every i != w
 * so k = (target - Bw) / (o - 1), clamped at 0.
 *
 * `worstCase` is RECOMPUTED from the resulting book rather than assumed to be
 * the target. Lifting one runner to a target drags every other runner down by
 * the stake, and on a three-way market the target routinely stops being the
 * worst outcome — a calculator that reported the target as the worst case would
 * be wrong exactly when it mattered.
 *
 * Returns null rather than throwing when the price is unusable or the selection
 * is unknown, matching this module's total-function style.
 */
export function hedgeToTarget(
  positions: BookPosition[],
  selection: string,
  price: number,
  target: number,
): HedgeResult | null {
  if (!Number.isFinite(price) || price <= 1.01) return null
  if (!Number.isFinite(target)) return null
  const index = positions.findIndex((p) => p.name === selection)
  if (index === -1) return null

  const current = positions[index]!.net
  const stake = Math.max(0, round2((target - current) / (price - 1)))
  const next = positions.map((p, i) => ({
    name: p.name,
    net: i === index ? round2(p.net + stake * (price - 1)) : round2(p.net - stake),
  }))
  const worstCase = round2(Math.min(...next.map((p) => p.net)))

  return {
    selection,
    price,
    stake,
    positions: next,
    worstCase,
    // A paise of tolerance: the stake is rounded to paise, so landing exactly on
    // the target is not always representable and a 1-paise undershoot is not an
    // overshoot worth warning about.
    overshoots: worstCase < round2(target - 0.01),
  }
}

/**
 * The target at which backing `selection` levels it with the lowest other
 * runner — the "flatten the book" figure.
 *
 * With m = min over i != w of Bi:
 *   Bw + k(o-1) = X  and  m - k = X   =>   X = (Bw + m(o-1)) / o
 * which reduces to exact equalisation on a two-runner market and generalises to
 * n runners. Null when there is no other runner to level against.
 */
export function flattenTarget(
  positions: BookPosition[],
  selection: string,
  price: number,
): number | null {
  if (!Number.isFinite(price) || price <= 1.01) return null
  const index = positions.findIndex((p) => p.name === selection)
  if (index === -1 || positions.length < 2) return null
  const others = positions.filter((_, i) => i !== index).map((p) => p.net)
  const lowestOther = Math.min(...others)
  return round2((positions[index]!.net + lowestOther * (price - 1)) / price)
}

/* ------------------------------------------------------------ settlement -- */

/** Which constraint stopped a settlement moving its full amount. */
export type SettlementCap = 'none' | 'their-balance' | 'your-free'

export interface SettlementPlan {
  /** From the PARENT's point of view: `take` collects, `give` pays. */
  direction: 'give' | 'take'
  /** |settlement| — what zeroing this row requires. */
  wanted: number
  /** What the caps allow right now, floored to the paise. */
  possible: number
  /** Names the cap that bit. `none` means the full amount can move. */
  capped: SettlementCap
}

/**
 * The movement that takes one direct child's settlement to zero, and what the
 * real caps allow of it.
 *
 * `settlement` is signed FROM THE PARENT'S POINT OF VIEW: positive means the
 * parent is owed, so the parent TAKES; negative means the parent owes, so the
 * parent GIVES. That single signed number replaced the original product's two
 * mutually-exclusive Take and Give columns, and this function is the only place
 * the sign is turned back into a direction.
 *
 * Caps match Backend-UI Give/Take (no child creditLimit headroom on Give):
 *   take  — cannot exceed what the child actually holds
 *   give  — cannot exceed the parent's free-to-give (balance less exposure)
 *
 * `freeToGive` is a parameter rather than always derived so a bulk run can
 * thread a RUNNING budget through consecutive rows: giving to one child must
 * reduce what is available for the next, or a bulk run can overdraw the parent.
 *
 * Returns null for an already-settled row. A zero-amount transfer is not a
 * movement, and offering one is how an operator settles the same row twice.
 */
export function settlementPlan(
  account: Pick<Account, 'balance' | 'creditLimit' | 'settlement'>,
  self: Pick<Account, 'balance' | 'exposure'>,
  freeToGive: number = availableToGive(self.balance, self.exposure),
): SettlementPlan | null {
  const settlement = round2(account.settlement)
  if (settlement === 0) return null

  if (settlement > 0) {
    const wanted = settlement
    const cap = Math.max(0, floor2(account.balance))
    const possible = floor2(Math.min(wanted, cap))
    return {
      direction: 'take',
      wanted,
      possible,
      capped: possible < wanted ? 'their-balance' : 'none',
    }
  }

  const wanted = round2(-settlement)
  const free = Math.max(0, floor2(freeToGive))
  const possible = floor2(Math.min(wanted, free))
  return {
    direction: 'give',
    wanted,
    possible,
    capped: possible < wanted ? 'your-free' : 'none',
  }
}
